import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { BRollsSearchRetrieveProps } from '@goldcast/api/content';
import { bRollsSearchRetrieve } from '@goldcast/api/content';
import { VideoOverlayTrack, mapBRollItemToVideoOverlayTrack } from './types';
import useDebounce from '@/hooks/useDebounce';

interface UseVideoOverlaySearchReturn {
  search: (query: string, orientation?: 'landscape' | 'portrait' | 'square') => void;
  results: VideoOverlayTrack[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  totalResults: number;
  loadMore: () => Promise<void>;
  reset: () => void;
  searchedQuery: string;
}

interface SearchState {
  query: string;
  orientation: 'landscape' | 'portrait' | 'square';
  page: number;
  results: VideoOverlayTrack[];
  totalResults: number;
  loading: boolean;
  error: Error | null;
}

const DEFAULT_ORIENTATION: 'landscape' | 'portrait' | 'square' = 'landscape';
const DEFAULT_LIMIT = 10;

/**
 * Custom hook for searching video overlays via backend API
 *
 * Features:
 * - Debounced search (300ms delay)
 * - Pagination support with loadMore
 * - Error handling
 * - Maps BRollItem[] to VideoOverlayTrack[]
 */
export const useVideoOverlaySearch = (): UseVideoOverlaySearchReturn => {
  const [state, setState] = useState<SearchState>({
    query: '',
    orientation: DEFAULT_ORIENTATION,
    page: 1,
    results: [],
    totalResults: 0,
    loading: false,
    error: null
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const performSearch = useCallback(
    async (query: string, orientation: 'landscape' | 'portrait' | 'square', page: number, append: boolean = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const props: BRollsSearchRetrieveProps = {
          queryParams: {
            query,
            orientation,
            limit: DEFAULT_LIMIT
          },
          signal: abortControllerRef.current?.signal
        };

        const response = await bRollsSearchRetrieve(props);
        const tracks = response.results.map(mapBRollItemToVideoOverlayTrack);

        setState(prev => ({
          ...prev,
          results: append ? [...prev.results, ...tracks] : tracks,
          totalResults: response.total_results,
          loading: false,
          error: null,
          page: append ? prev.page + 1 : page
        }));
      } catch (error) {
        // Ignore AbortError - this is expected when a new search cancels the previous one
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        setState(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error : new Error('Search failed')
        }));
      }
    },
    []
  );

  const handleSearch = useCallback(
    (query: string, orientation: 'landscape' | 'portrait' | 'square') => {
      const trimmedQuery = query.trim();

      // Handle empty query by resetting state
      if (!trimmedQuery) {
        setState(prev => ({
          ...prev,
          query: '',
          results: [],
          totalResults: 0,
          page: 1,
          loading: false,
          error: null
        }));
        return;
      }

      performSearch(trimmedQuery, orientation, 1, false);
      setState(prev => ({ ...prev, query: trimmedQuery, orientation }));
    },
    [performSearch]
  );

  const debouncedSearch = useDebounce(handleSearch, 300);

  const search = useCallback(
    (query: string, orientation: 'landscape' | 'portrait' | 'square' = DEFAULT_ORIENTATION) => {
      debouncedSearch(query, orientation);
    },
    [debouncedSearch]
  );

  // Calculate if there are more results to load
  const hasMore = useMemo(() => state.results.length < state.totalResults, [state.results.length, state.totalResults]);

  const loadMore = useCallback(async () => {
    if (state.loading || !state.query.trim() || !hasMore) {
      return;
    }

    await performSearch(state.query, state.orientation, state.page + 1, true);
  }, [state.query, state.orientation, state.page, state.loading, hasMore, performSearch]);

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setState({
      query: '',
      orientation: DEFAULT_ORIENTATION,
      page: 1,
      results: [],
      totalResults: 0,
      loading: false,
      error: null
    });
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    search,
    results: state.results,
    loading: state.loading,
    error: state.error,
    hasMore,
    totalResults: state.totalResults,
    loadMore,
    reset,
    searchedQuery: state.query
  };
};
